import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelUtils } from '@frontend/api-call-intel';
import { getUser } from '@frontend/auth-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useHasCallIntelAclAccess, UseHasCallIntelAclAccessOptions } from './use-has-call-intel-acl-access';
import { useIsCallIntelEligibleAccount } from './use-is-call-intel-eligible-account';

type UseCallIntelFeatureAccessOptions = {
  permissions?: Permission[];
  featureFlagKey?: string;
  aclOptions?: UseHasCallIntelAclAccessOptions;
  /**
   * If true, Weave demo users bypass ACL permission checks and subscription requirements.
   * When enabled, demo users get access if feature flags pass.
   */
  allowDemoOverride?: boolean;
  /**
   * If true, includes AI Receptionist (Voice AI) locations when checking Call Intel eligibility.
   */
  includeAiReceptionist?: boolean;
};

export const useCallIntelFeatureAccess = ({
  permissions = [],
  featureFlagKey,
  aclOptions,
  allowDemoOverride = false,
  includeAiReceptionist = true,
}: UseCallIntelFeatureAccessOptions = {}) => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const username = getUser()?.username?.toLowerCase() ?? '';
  const isDemoUser = useMemo(() => CallIntelUtils.isDemoUser(username), [username]);

  const hasPermission = useHasCallIntelAclAccess(permissions, {
    ...aclOptions,
    allowDemoOverride,
  });
  const { isCallIntelActive: hasCallIntelFeature } = useIsCallIntelEligibleAccount({
    includeAiReceptionist,
  });

  const isFlagEnabled = featureFlagKey ? getFeatureFlagValue(featureFlagKey) : true;

  const isAccessible =
    (allowDemoOverride && isDemoUser && isFlagEnabled) || (hasPermission && hasCallIntelFeature && isFlagEnabled);

  const shouldHideFeature = !isAccessible;

  return {
    isAccessible,
    hasPermission,
    hasCallIntelFeature,
    isFlagEnabled,
    isDemoUser,
    shouldHideFeature,
  };
};
